/**
 * BrokermandatMeldungPage - Administration › Brokermandat-Meldung (Stefan P5 V2.0 Kap. 7.10).
 *
 * The Meldemodus (automatisch / Vorschlag mit Freigabe), the Eskalationsziel of the
 * Eintragungskontrolle (a Funktionsgruppe; empty = the routing default) and the switch that lets a
 * clean return confirm the Mandatsumfang without the broker's review; the Fristen stay on their own
 * page (linked). Read tenant.read, save tenant.manage (the api is the hard line).
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { REGISTRATION_SETTINGS_REPORT_MODE_LABEL, REGISTRATION_SETTINGS_REPORT_MODE_VALUES } from '@brokly/shared';
import type { BrokerRegistrationSettingsDto, RegistrationSettingsReportMode } from '@brokly/shared';
import { Button } from '../../../components/ui/Button';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { useFunktionsgruppen } from '../hooks';
import { useEffectivePermissions } from '../useEffectivePermissions';
import { useBrokerRegistrationSettings, useSetBrokerRegistrationSettings } from '../../mandate/mandate-registration-queries';

const meta = ADMIN_PAGE_META['brokermandat-meldung'];

type Edits = Partial<Pick<BrokerRegistrationSettingsDto, 'report_mode' | 'escalation_funktionsgruppe_id' | 'scope_auto_confirm'>>;

export function BrokermandatMeldungPage() {
  const settings = useBrokerRegistrationSettings();
  const save = useSetBrokerRegistrationSettings();
  const { can } = useEffectivePermissions();
  const canManage = can('tenant.manage');
  // Each control only with the key its route needs: the group list, the Fristen page.
  const canReadGroups = can('funktionsgruppe.read');
  const canEditFristen = can('mahnwesen_config.manage');
  const groups = useFunktionsgruppen(canReadGroups);
  const [edits, setEdits] = useState<Edits>({});

  if (settings.isError) {
    return (
      <div>
        <AdminPageHeader title={meta.title} description={meta.description} />
        <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertTriangle className="size-4" />
          Die Einstellungen konnten nicht geladen werden.
        </p>
      </div>
    );
  }
  if (settings.data === undefined) {
    return (
      <div>
        <AdminPageHeader title={meta.title} description={meta.description} />
        <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
          <Loader2 className="size-4 animate-spin" /> Lädt …
        </p>
      </div>
    );
  }
  const current = { ...settings.data, ...edits };

  function onSave() {
    save.mutate(
      {
        report_mode: current.report_mode,
        escalation_funktionsgruppe_id: current.escalation_funktionsgruppe_id,
        scope_auto_confirm: current.scope_auto_confirm,
      },
      { onSuccess: () => setEdits({}) },
    );
  }

  return (
    <div>
      <AdminPageHeader title={meta.title} description={meta.description} />
      <section className="flex flex-col gap-4 rounded-lg border bg-card p-4" aria-label="Brokermandat-Meldung">
        {current.ist_default && (
          <span className="w-fit rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground" data-testid="bmm-standard">
            Standard
          </span>
        )}
        <label className="flex flex-col gap-1 text-sm">
          Meldemodus
          <select
            className="w-72 rounded border px-2 py-1 text-sm"
            aria-label="Meldemodus"
            value={current.report_mode}
            disabled={!canManage}
            onChange={(e) => setEdits((p) => ({ ...p, report_mode: e.target.value as RegistrationSettingsReportMode }))}
          >
            {REGISTRATION_SETTINGS_REPORT_MODE_VALUES.map((v) => (
              <option key={v} value={v}>
                {REGISTRATION_SETTINGS_REPORT_MODE_LABEL[v]}
              </option>
            ))}
          </select>
          <span className="text-xs text-muted-foreground">
            Automatisch: die Meldung geht nach dem Abschluss sofort an die Versicherer, Ausnahmen gehen zur Freigabe.
            Vorschlag mit Freigabe: jede Meldung wartet auf die Freigabe durch den Broker.
          </span>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Eskalationsziel
          <select
            className="w-72 rounded border px-2 py-1 text-sm"
            aria-label="Eskalationsziel"
            value={current.escalation_funktionsgruppe_id ?? ''}
            disabled={!canManage}
            onChange={(e) => setEdits((p) => ({ ...p, escalation_funktionsgruppe_id: e.target.value === '' ? null : e.target.value }))}
          >
            <option value="">Standard (Routing der Aufgaben)</option>
            {(canReadGroups ? groups.data?.funktionsgruppen ?? [] : []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            aria-label="Mandatsumfang ohne Prüfung bestätigen"
            checked={current.scope_auto_confirm}
            disabled={!canManage}
            onChange={(e) => setEdits((p) => ({ ...p, scope_auto_confirm: e.target.checked }))}
          />
          Mandatsumfang ohne Prüfung bestätigen, wenn der Rücklauf keine Änderung zeigt
        </label>
        <p className="text-xs text-muted-foreground">
          Frist 1 {current.frist_1} und Frist 2 {current.frist_2} Arbeitstage
          {canEditFristen && (
            <>
              :{' '}
              <Link to="/admin/$section" params={{ section: 'versicherer-fristen' }} className="underline">
                Versicherer-Fristen anpassen
              </Link>
            </>
          )}
        </p>
        {save.isError && (
          <p className="text-sm text-destructive" role="alert">
            Die Einstellungen konnten nicht gespeichert werden.
          </p>
        )}
        {canManage && (
          <Button size="sm" className="w-fit" disabled={save.isPending} onClick={onSave}>
            Speichern
          </Button>
        )}
      </section>
    </div>
  );
}
